import {Injectable} from '@angular/core';
import {convertToParamMap, Router} from '@angular/router';
import {Actions, createEffect,  ofType} from '@ngrx/effects';
import {Action, select, Store} from '@ngrx/store';
import * as _ from 'lodash';
import * as fromContracts from '../reducers';
import {catchError, delayWhen, filter, map, switchMap, withLatestFrom} from 'rxjs/operators';
import {BaseEffect} from '../../shared/effects/base.effect';
import * as fromRoot from '../../shared/reducers';
import {ArrayResponse, cleanObject, DEFAULT_PAGE_SIZE, go, SendBackResult} from '../../shared';
import {debug} from '../../../rxjs-operators';
import {
  ChangeSubListPageSizeAction, ExportAnomalyTaskSuccessAction, ExportExcelAllSubSuccessAction, LoadSubListFailAction, LoadSubListSuccessAction,
  OrderSubListByAction,
  PageSubListToAction,
  QuickSearchSubListAction, SearchSubListAction, SendEmailFinanceAction, SendEmailFinanceSuccessAction,
  subListActionTypes
} from '../actions/subscription-list';
import {SubscriptionsList} from '../models/subscription';
import {SubscriptionsListService} from '../services/subscriptions-list.service';
import {ExcelService} from '../services/excel.service';
import * as moment from 'moment';
import { L10nTranslationService } from 'angular-l10n';
import { success } from '../actions/toasts';
import {DuplicationRuleAction} from '../actions/rule-duplication';

@Injectable({providedIn: 'root'})
export class SubListListEffect extends BaseEffect {
  private routerState$ = this.store.pipe(select(fromRoot.selectors.getRouterState) , filter(state => !_.isEmpty(state)));
  private currentClient$ = this.store.pipe(select(fromRoot.selectors.getCurrentClient), filter(client => !_.isEmpty(client)));


  pageTo$ = createEffect(() => { return this.actions$.pipe(ofType<PageSubListToAction>(subListActionTypes.PAGE_TO),
    withLatestFrom(this.routerState$.pipe(map(state => state.queryParams))),
    map(([action, query]) => Object.assign({}, query, { $page: action.payload })),
    map(query => go([], query)))
  });


  changePageSize$ = createEffect(() => { return this.actions$.pipe(ofType<ChangeSubListPageSizeAction>(subListActionTypes.CHANGE_PAGE_SIZE),
    withLatestFrom(this.routerState$.pipe(map(state => state.queryParams))),
    map(([action, query]) => Object.assign({}, query, { $length: action.payload })),
    map(query => go([], query)))
  });


  orderBy$ = createEffect(() => { return this.actions$.pipe(ofType<OrderSubListByAction>(subListActionTypes.ORDER_BY),
    withLatestFrom(this.routerState$.pipe(map(state => state.queryParams))),
    map(([action, query]) => Object.assign({}, query, { $orderBy: action.payload })),
    map(query => go([], query)))
  });


  quickSearch$ = createEffect(() => { return this.actions$.pipe(ofType<QuickSearchSubListAction>(subListActionTypes.QUICK_SEARCH),
    withLatestFrom(this.routerState$.pipe(map(state => state.queryParams))),
    map(([action, query]) => Object.assign({}, query, { quickSearch: action.payload, $page: 1 })),
    map(query => cleanObject(query, ['$page', '$length', '$orderBy', 'quickSearch' , 'anomalyType'], true)),
    map(query => go([], query)))
  });


  search$ = createEffect(() => { return this.actions$.pipe(ofType<SearchSubListAction>(subListActionTypes.SEARCH),
    withLatestFrom(this.routerState$.pipe(map(state => state.queryParams))),
    map(([action, query]) => Object.assign({}, cleanObject(query, ['$page', '$length', '$orderBy' , 'anomalyType'], true), action.payload, { $page: 1 })),
    map(query => go([], query)))
  });


  load$ = createEffect(() => { return this.actions$.pipe(ofType(subListActionTypes.LOAD),
    delayWhen(() => this.routerState$),
    debug('Load ssub list action received.'),
    withLatestFrom(this.routerState$.pipe(map(state => state.queryParams))),
    map(([action, query]) => Object.assign({}, { $page: 1, $length: DEFAULT_PAGE_SIZE }, query)),
    withLatestFrom(this.routerState$.pipe(
      map(state => state.params),
      map(convertToParamMap),
      map(params => params.get('companyId')))),
    switchMap(([filterD, uuid]) => this.subscriptionsListService.getList(uuid, filterD).pipe(
      map((payload: SendBackResult<ArrayResponse<SubscriptionsList>>) => new LoadSubListSuccessAction(payload.data)),
      catchError(res => this.catchResponseError(res)))))
  });
  loadSubscriptionBySite$ = createEffect(() => { // @ts-ignore

    return this.actions$.pipe(ofType(subListActionTypes.LOAD_MULTI_SUBSCRIPTION_BY_SITE),
      delayWhen(() => this.routerState$),
      debug('Load ssub list action received.'),
      withLatestFrom(this.routerState$.pipe(map(state => state.queryParams))),
      map(([action, query]) => Object.assign({}, { $page: 1, $length: DEFAULT_PAGE_SIZE }, query)),
      withLatestFrom(this.routerState$.pipe(
        map(state => state.params),
        map(convertToParamMap),
        map(params => params.get('id')))),
      delayWhen(() => this.currentClient$),
      withLatestFrom(this.currentClient$),
    switchMap(([filterD, client]) => this.subscriptionsListService.getSubscriptionListBySite(filterD[0] , filterD[1], client.uuid).pipe(
      map((payload: SendBackResult<ArrayResponse<SubscriptionsList>>) => new LoadSubListSuccessAction(payload.data)),
      catchError(res => this.catchResponseError(res)))))
  });


  exportExcel$ = createEffect(() => { return this.actions$.pipe(ofType(subListActionTypes.EXPORT_ALL_SUBS),
    delayWhen(() => this.routerState$),
    debug('Load ssub list action received.'),
    withLatestFrom(this.routerState$.pipe(map(state => state.queryParams))),
    map(([action, query]) => Object.assign({}, query, { $page: 1, $length: -1 })),
    withLatestFrom(this.routerState$.pipe(
      map(state => state.params),
      map(convertToParamMap),
      map(params => params.get('companyId')))),
    switchMap(([filterD, uuid]) => this.subscriptionsListService.getList(uuid, filterD).pipe(
      // tslint:disable-next-line:no-any
      map((payload: SendBackResult<any>) => this.exportExcelData(payload.data)))))
  });
  exportExcelClientSite$ = createEffect(() => { return this.actions$.pipe(ofType(subListActionTypes.EXPORT_CLIENT_SITE_SUBSCRIPTION),
    delayWhen(() => this.routerState$),
    debug('Load ssub list action received.'),
    withLatestFrom(this.routerState$.pipe(map(state => state.queryParams))),
    map(([action, query]) => Object.assign({}, query, { $page: 1, $length: -1 })),
    delayWhen(() => this.currentClient$),
    withLatestFrom(this.currentClient$),
    switchMap(([filterD, client]) => this.subscriptionsListService.getListByClient(client.uuid, filterD).pipe(
      // tslint:disable-next-line:no-any
      map((payload: SendBackResult<any>) => this.exportExcelData(payload.data)))))
  });

  createAnomalyExportTask$ = createEffect(() => { return this.actions$.pipe(ofType(subListActionTypes.EXPORT_ANOMALY_TASK),
    debug('Load export anomaly task action received.'),
    switchMap(() => this.subscriptionsListService.createAnomalyTask().pipe(
      // tslint:disable-next-line:no-any
      map((payload: SendBackResult<any>) => new ExportAnomalyTaskSuccessAction()),
      catchError(res => this.catchResponseError(res)))))
  });
  sendEmailFinanceAction$ = createEffect(() => { return this.actions$.pipe(ofType(subListActionTypes.SEND_EMAIL_FINANCE),
    debug('Load export anomaly task action received.'),
    map((action: SendEmailFinanceAction) => action.payload ),
    switchMap((data) => this.subscriptionsListService.sendEmailAnomaly(data).pipe(
      // tslint:disable-next-line:no-any
      map((payload: SendBackResult<any>) => new SendEmailFinanceSuccessAction()),
      catchError(res => this.catchResponseError(res)))))
  });
  sendEmailFinanceActionSuccess$ = createEffect(() => { return this.actions$.pipe(
    ofType(subListActionTypes.SEND_EMAIL_FINANCE_SUCCESS),
    debug('success export anomaly task action received.'),
    switchMap((data) => [
      success(this.translation.translate('SEND_EMAIL_FINANCE_SUCCESS'), this.translation.translate('TOAST_SUCCESS_TITLE'))
    ]),
  )
  });

  createAnomalyExportTaskSuccess$ = createEffect(() => { return this.actions$.pipe(
    ofType(subListActionTypes.EXPORT_ANOMALY_TASK_SUCCESS),
    debug('success export anomaly task action received.'),
    switchMap((data) => [
      success(this.translation.translate('EXPORT_ANOMALY_TASK_SUCCESS'), this.translation.translate('TOAST_SUCCESS_TITLE'))
    ]),
  )
  });

  loadAnomalies$ = createEffect(() => { return this.actions$.pipe(ofType(subListActionTypes.LOAD_ANOMALY),
    delayWhen(() => this.routerState$),
    debug('Load anomalies sub list action received.'),
    withLatestFrom(this.routerState$.pipe(map(state => state.queryParams))),
    map(([action, query]) => Object.assign({}, { $page: 1, $length: DEFAULT_PAGE_SIZE }, query)),
    switchMap((filterD) => this.subscriptionsListService.getAnomaliesList(filterD).pipe(
      map((payload: SendBackResult<ArrayResponse<SubscriptionsList>>) => new LoadSubListSuccessAction(payload.data)),
      catchError(res => this.catchResponseError(res)))))
  });

  loadSubByContract$ = createEffect(() => { return this.actions$.pipe(ofType(subListActionTypes.LOAD_SUBSCRIPTION_BY_CONTRACT),
    delayWhen(() => this.routerState$),
    debug('Load sub by contract action received.'),
    withLatestFrom(this.routerState$.pipe(map(state => state.queryParams))),
    map(([action, query]) => Object.assign({}, { $page: 1, $length: DEFAULT_PAGE_SIZE} , query)),
    withLatestFrom(this.routerState$.pipe(
      map(state => state.params),
      map(convertToParamMap),
      map(params => params.get('contractId')))),
    switchMap(([filterD, uuid]) => this.subscriptionsListService.getListByContract(uuid, filterD).pipe(
      map((payload: SendBackResult<ArrayResponse<SubscriptionsList>>) => new LoadSubListSuccessAction(payload.data)),
      catchError(res => this.catchResponseError(res)))))
  });

  loadSitesByClient$ = createEffect(() => { return  this.actions$.pipe(ofType(subListActionTypes.LOAD_SUBSCRIPTION_BY_CLIENT),
    delayWhen(() => this.routerState$),
    debug('Load anomalies sub list action received.'),
    withLatestFrom(this.routerState$.pipe(map(state => state.queryParams))),
    map(([action, query]) => Object.assign({}, { $page: 1, $length: DEFAULT_PAGE_SIZE }, query)),
    delayWhen(() => this.currentClient$),
    withLatestFrom(this.currentClient$),
    switchMap(([filterD, client]) => this.subscriptionsListService.getListByClient(client.uuid , filterD).pipe(
      map((payload: SendBackResult<ArrayResponse<SubscriptionsList>>) => new LoadSubListSuccessAction(payload.data)),
      catchError(res => this.catchResponseError(res)))))
  });
  loadSitesByClientSimple$ = createEffect(() => { return  this.actions$.pipe(ofType(subListActionTypes.LOAD_SUBSCRIPTION_BY_CLIENT_SIMPLE),
    delayWhen(() => this.routerState$),
    debug('Load anomalies sub list action received.'),
    withLatestFrom(this.routerState$.pipe(map(state => state.queryParams))),
    map(([action, query]) => Object.assign({}, { $page: 1, $length: DEFAULT_PAGE_SIZE }, query)),
    delayWhen(() => this.currentClient$),
    withLatestFrom(this.currentClient$),
    switchMap(([filterD, client]) => this.subscriptionsListService.getListByClientSimple(client.uuid , filterD).pipe(
      map((payload: SendBackResult<ArrayResponse<SubscriptionsList>>) => new LoadSubListSuccessAction(payload.data)),
      catchError(res => this.catchResponseError(res)))))
  });



  constructor(private actions$: Actions,
              private store: Store<fromContracts.AppState>,
              private subscriptionsListService: SubscriptionsListService,
              private translation: L10nTranslationService,
              private excelService: ExcelService,
              router: Router) {
    super(router);
  }
  protected handleUnhandledError = (response) => new LoadSubListFailAction(response['error'].message)

  protected handleGoneError = (response) => {
    return new LoadSubListFailAction(response['error'].message);
  }
  // tslint:disable-next-line:no-any
  exportExcelData(data: any): Action {
    const sitesCol = [];
    for (let i = 0; i < data.array.length; i++) {
      sitesCol.push({
        BillingLinkId: data.array[i].id,
        subscriptionUuid: data.array[i].subscriptionUuid,
        Service: this.getServiceStatus(data.array[i].audio , data.array[i].video),
        SubscriptionName: data.array[i].subscriptionName,
        Status: this.getSubStatus(data.array[i].status),
        Client: data.array[i].clientName,
        'Start date': data.array[i].startBilling ? moment(data.array[i].startBilling).format('YYYY-MM-DD') : '' ,
        'End date': data.array[i].endBilling ? moment(data.array[i].endBilling).format('YYYY-MM-DD') : '' ,
        Delivery: data.array[i].deliveryDate ? moment(data.array[i].deliveryDate).format('YYYY-MM-DD') : '' ,
        Price: data.array[i].price ? data.array[i].price + '€' : '' ,
        Contract: data.array[i].contractName,
        Product: data.array[i].productName ,
        playerName: data.array[i].playerName ,
        'Last Invoice Date': data.array[i].lastInvoiceCalculation ? moment(data.array[i].lastInvoiceCalculation).format('YYYY-MM-DD') : '' ,
      })
    }

    this.excelService.exportAsExcelFile(sitesCol, 'Subscription_by_company');
    return new ExportExcelAllSubSuccessAction();
  }

  getSubStatus(status: number): string {
    const statusArray = [
      { id: 3, name: 'Exploit' },
      { id: 2, name: 'Pilot' },
      { id: 1, name: 'Off' },
      { id: 0, name: 'To off - no update' },
      { id: -1, name: 'Stopped' },
      { id: -2, name: 'Bankruptcy' }
    ]
    for (let j = 0; j < statusArray.length; j++) {
      if (status === statusArray[j].id ) {
        return statusArray[j].name
      }
    }
  }
  getServiceStatus(audio: boolean, video: boolean): string {
   if (audio && video)  {
     return 'Audio and Video'
   } else  if (audio && !video)  {
     return 'Audio';
   } else  if (!audio && video)  {
     return 'Video';
   } else {
     return '';
   }
  }
}
